export enum ProductStatus {
    ACTIVE   = 'ACTIVE',
    PAUSED   = 'PAUSED',
    DISABLED = 'DISABLED',
}

export class ProductPricing {
    constructor(
        public sellingPrice: number = 0,
        public cost:         number = 0,
        public currency:     string = 'ILS'
    ) {}

    static fromObject(o: any = {}): ProductPricing {
        return new ProductPricing(
            typeof o.sellingPrice === 'number' ? o.sellingPrice : 0,
            typeof o.cost         === 'number' ? o.cost         : 0,
            o.currency ?? 'ILS'
        );
    }
}

export class ProductInventory {
    constructor(
        public stockOnHand:  number = 0,
        public reorderLevel: number = 0,
        public weight:       string = ''
    ) {}

    static fromObject(o: any = {}): ProductInventory {
        return new ProductInventory(
            typeof o.stockOnHand  === 'number' ? o.stockOnHand  : 0,
            typeof o.reorderLevel === 'number' ? o.reorderLevel : 0,
            o.weight ?? ''
        );
    }
}

export class ProductCategory {
    constructor(
        public id:   string = '',
        public name: string = '',
        public url:  string = ''
    ) {}

    static fromObject(o: any = {}): ProductCategory {
        return new ProductCategory(
            o.id   ?? '',
            o.name ?? '',
            o.url  ?? ''
        );
    }
}

export class ProductSEO {
    constructor(
        public title:       string = '',
        public description: string = '',
        public keyword:     string = ''
    ) {}

    static fromObject(o: any = {}): ProductSEO {
        return new ProductSEO(
            o.title       ?? '',
            o.description ?? '',
            o.keyword     ?? ''
        );
    }
}

export class Product {
    constructor(
        public id:              string          = '',
        public handle:          string          = '',
        public sku:             string          = '',
        public name:            string          = '',
        public brand:           string          = '',
        public image:           string          = '',
        public hoverImage:      string          = '',
        public previewImage:    string          = '',
        public images:          string[]        = [],
        public pricing:         ProductPricing  = new ProductPricing(),
        public inventory:       ProductInventory = new ProductInventory(),
        public category:        ProductCategory = new ProductCategory(),
        public categoryIds:     string[]        = [],
        public description:     string          = '',
        public longDescription: string          = '',
        public specifications:  Record<string, any> = {},
        public seo:             ProductSEO      = new ProductSEO(),
        public tags:            string[]        = [],
        public showInStore:     boolean         = true,
        public status:          string          = ProductStatus.ACTIVE,
        public color:           string          = '',
        public wireDiameter:    string          = '',
        public innerDiameter:   string          = '',
        public coilLength:      string          = '',
        public side:            string          = '',
        public variants:        any[]           = [],

    ) {}

    static createDefault(): Product {
        return new Product();
    }

    static fromObject(o: any = {}): Product {
        const cat = ProductCategory.fromObject(o.category);
        const categoryIds: string[] = Array.isArray(o.categoryIds)
            ? o.categoryIds
            : (cat.id ? [cat.id] : []);

        return new Product(
            o.id              ?? '',
            o.handle          ?? '',
            o.sku             ?? '',
            o.name            ?? '',
            o.brand           ?? '',
            o.image           ?? '',
            o.hoverImage      ?? '',
            o.previewImage    ?? '',
            Array.isArray(o.images) ? o.images : [],
            ProductPricing.fromObject(o.pricing),
            ProductInventory.fromObject(o.inventory),
            cat,
            categoryIds,
            o.description     ?? '',
            o.longDescription ?? '',
            o.specifications && typeof o.specifications === 'object' ? o.specifications : {},
            ProductSEO.fromObject(o.seo),
            Array.isArray(o.tags) ? o.tags : [],
            typeof o.showInStore === 'boolean' ? o.showInStore : true,
            o.status ?? ProductStatus.ACTIVE,
            o.color  ?? '',
            o.wireDiameter  ?? '',
            o.innerDiameter ?? '',
            o.coilLength    ?? '',
            o.side          ?? '',
            Array.isArray(o.variants) ? o.variants : [],
        );
    }

    // ─── Price Getters ────────────────────────────────────────────────────────────

    get isShowPrice(): boolean {
        return this.pricing.sellingPrice > 0;
    }

    get isDiscountPrice(): boolean {
        return this.pricing.cost > 0 && this.pricing.cost > this.pricing.sellingPrice;
    }

    get finalPrice(): number {
        return Number(this.pricing.sellingPrice.toFixed(2));
    }

    get originalPrice(): number {
        return Number(this.pricing.cost.toFixed(2));
    }

    // ─── Stock Getters ────────────────────────────────────────────────────────────

    get isInStock(): boolean {
        return this.inventory.stockOnHand > 0;
    }

    get isOutOfStock(): boolean {
        return !this.isInStock;
    }
}
